import { POST } from '@/api'
import {
  addId,
  initStatus,
  filterByIsShow,
  updateShowStatus,
  setAnnotations,
  toShortLabel,
  getAnnotationConfigs,
} from '@/utils/analysis'
import { arrayToRepeater } from '@/utils/array'

export default {
  namespaced: true,
  state: {
    qtyChart: null,
    table: null,
    dashboard: null,
    busy: false,
  },
  modules: {},
  mutations: {
    set(state, data) {
      state.qtyChart = data.charts?.qty ?? null
      state.dashboard = data.dashboard
      state.table = {
        ...state.table,
        header: data.table?.header,
        selectedIds: state.table?.selectedIds || data.table?.selectedIds,
        value: initStatus(addId(data.table?.value)),
      }
    },
    busy(state, busy) {
      state.busy = busy
    },
    toggle(state, id) {
      state.table.value = updateShowStatus(state.table.value, id)
    },
    setSelectedIds(state, ids) {
      state.table.selectedIds = ids
      state.table.value = initStatus(state.table.value)
    },
  },
  getters: {
    chart({ qtyChart, table }, { data }) {
      return unit => {
        if (!qtyChart) {
          return null
        }

        // 表示されるカラムのみ抽出
        const filteredChart = filterByIsShow(data, table.header.aggregatedValue, ['group', 'qty'])

        // ラベルを表示用に変換する
        const [columns, ...rows] = filteredChart.map(([term, ...rest], idx) => [
          idx === 0 ? term : toShortLabel(term, unit),
          ...rest,
        ])

        // 注訳を追加
        const { definitions, values } = getAnnotationConfigs('point')
        return [setAnnotations(columns, ...definitions), ...rows.map(row => setAnnotations(row, ...values))]
      }
    },
    dashboardChart({ qtyChart }, rootState, getters, { 'analysis/colors/chartBox': { first } }) {
      if (qtyChart === null) {
        return null
      }

      let [columns, ...rows] = qtyChart
      columns = [...columns, { type: 'string', role: 'annotation' }, { role: 'style' }]
      rows = rows.map(([rank, value]) => [rank, value, value, null])

      // 先頭だけ色を変える
      rows[0][3] = first
      return [columns, ...rows]
    },
    viewWindowMaxValue({ qtyChart }) {
      if (qtyChart === null) {
        return 0
      }
      const rate = 1 + String(qtyChart[1][1]).length * 0.06
      // 1位の文字がチャートのエリアの外に書き出されて表示されないため、表示枠を明示的に拡張する。
      return qtyChart[1][1] * rate
    },
    header(state) {
      return state.table?.header ?? null
    },
    data(state) {
      return state.table?.value?.filter(v => state.table.selectedIds.includes(v.id)) ?? []
    },
    hAxis({ qtyChart }) {
      return qtyChart.map(([term]) => term).filter(Boolean) ?? []
    },
    detail({ qtyChart, dashboard }) {
      return {
        number: qtyChart?.[1]?.[1] ?? null,
        name: qtyChart?.[1]?.[0] ?? null,
        fluctuationType: dashboard?.mom?.fluctuationType ?? null,
        fluctuationValue: dashboard?.mom?.fluctuationValue ?? null,
      }
    },
    colors(state, { data }, rootState, { 'analysis/colors/tag': colors }) {
      return data.map(arrayToRepeater(colors))
    },
    chartColors(state, { colors, statuses }) {
      return statuses.reduce(
        (accumulator, status, index) => (status ? [...accumulator, colors[index]] : accumulator),
        [],
      )
    },
    statuses(state, { data }) {
      return data.map(({ isShow }) => isShow) ?? []
    },
  },
  actions: {
    async get({ commit, state }, { unit, startTerm, endTerm, dashboardFlg }) {
      if (state.busy) return
      commit('busy', true)

      const { status, message, data } = await POST('/api/web/analysis/reservation/tag', {
        unit,
        startTerm,
        endTerm,
        dashboardFlg: dashboardFlg || 0,
      })

      if (status === 200) {
        commit('set', data)
      } else {
        commit('app/error', { status, message }, { root: true })
      }

      commit('busy', false)
    },
  },
}
